<script setup lang="ts" generic="Content extends ContentRegistryContent">
import { computed } from 'vue';
import type { Category } from '@ilteducation/content-ref-data-types';
import { useVideos, Video } from '@ilteducation/videos';
import { storeToRefs } from 'pinia';
import { useCategories } from '@ilteducation/content-ref-data';
import { useLocale } from '@/plugins/translation';
import { AddCollectionButton } from '@ilteducation/components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useDisplay } from 'vuetify';
import type { ArticleV1, Content as ContentRegistryContent, MaterialV1 } from '@ilteducation/content-registry-types';
import { apiGetPreviewUrl } from '@ilteducation/content';

const emit = defineEmits<{
  (e: 'toggleSelect'): void;
  (e: 'addContent'): void;
  (e: 'grab'): void;
  (e: 'place'): void;
}>();

type Props = {
  property?: string;
  item: Content;
  isSelected: boolean;
  isEditing?: boolean;
  isSelecting?: boolean;
  isOrdering?: boolean;
  isAdding?: boolean;
  isContentInAdded?: boolean;
  subjectId?: string;
  isGrabbed?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  isSelected: false,
  isEditing: false,
  isSelecting: false,
  isAdding: false,
  isOrdering: false,
  isContentInAdded: false,
});

const { t, l } = useLocale();

// `title` below, not `item.title`: a video's raw title is a localised array, which read out as JSON.
const handleLabel = computed(() =>
  t(props.isGrabbed ? l.components.reorder_item_put_down : l.components.reorder_item_pick_up, {
    title: title.value,
  }),
);
const { xs } = useDisplay();

const isVideo = (content: Video | ContentRegistryContent): content is Video => content.type === 'VideoV1';
const isArticle = (content: Video | ContentRegistryContent): content is ArticleV1 => content.type === 'ArticleV1';
const isMaterial = (content: Video | ContentRegistryContent): content is MaterialV1 => content.type === 'MaterialV1';

const videoStore = useVideos();
const { videos } = storeToRefs(videoStore);
const { byId: getCategoryById } = useCategories();

const addContent = () => {
  emit('addContent');
};

const grades = computed(() => {
  if (!isVideo(content.value) && !isMaterial(content.value)) {
    return '';
  }
  const categories = content.value.categories || [];
  return categories
    .map((category) => getCategoryById.value(category))
    .filter((c): c is Category => !!c && c.kind === 'grade')
    .sort((a, b) => a.category.localeCompare(b.category))
    .map((c) => {
      const translationKey = `grades.${[c.categoryCode]}`;
      return t(translationKey) === translationKey ? c.category : t(translationKey);
    })
    .join(', ');
});

const subjectText = computed(() => {
  if (!props.subjectId) {
    return '';
  }
  const subject = getCategoryById.value(props.subjectId);
  if (!subject) {
    return '';
  }
  return ` • ${subject.category}`;
});

const content = computed<Video | ArticleV1 | MaterialV1>(() => {
  switch (props.item.type) {
    case 'VideoV1':
      return videos.value[props.item.id];
    case 'ArticleV1':
      return props.item as ArticleV1;
    case 'MaterialV1':
      return props.item as MaterialV1;
    default:
      throw new Error(`Unsupported content type: ${props.item.type}`);
  }
  // return props.item.type === 'VideoV1' ? videos.value[props.item.id] : (props.item as ArticleV1);
});
const title = computed(() => {
  if (isVideo(content.value) || isMaterial(content.value)) {
    return content.value.title;
  } else if (isArticle(content.value)) {
    return content.value.headline;
  } else {
    return '';
  }
});
const thumbnail = computed(() => {
  if (isVideo(content.value)) {
    return content.value.thumbnail;
  } else if (isArticle(content.value)) {
    return content.value.cover?.url || content.value.posterUrl;
  } else if (isMaterial(content.value)) {
    return apiGetPreviewUrl(content.value.id, 500); // define the image height for the preview from server;
  }
  return '';
});
const stripHtml = (html: string): string => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || '').replace(/\s+/g, ' ').trim();
};
const description = computed(() => {
  const raw = content.value.description || '';
  return isMaterial(content.value) ? stripHtml(raw) : raw;
});
const publisher = computed(() => ('publisher' in content.value ? content.value.publisher : ''));
const contentIcon = computed(() => {
  if (isVideo(content.value)) {
    return 'fa:fas fa-video-camera';
  } else if (isArticle(content.value)) {
    return 'fa:fas fa-newspaper';
  } else if (isMaterial(content.value)) {
    return 'fa:fas fa-file-lines';
  } else return 'fa:fas fa-file-lines';
});
const contentIconColor = computed(() => {
  if (isArticle(content.value)) {
    return '#db335d';
  }
  return 'brand';
});
</script>

<template>
  <template v-if="content">
    <!-- No aria-label here: a plain div cannot take a name, and screen readers that honour it
         anyway read the title an extra time before the row's own text. -->
    <div class="item-container d-flex flex-nowrap align-start" :class="{ 'edit-mode': isOrdering }">
      <div class="thumb my-3 pr-sm-4 pr-2">
        <v-img :cover="isVideo(content)" :src="thumbnail || ''" alt="">
          <template #placeholder>
            <v-skeleton-loader type="image" />
          </template>
          <template v-if="isOrdering">
            <div class="fill-height thumb-overlay"></div>
            <!-- The grip picks the row up; while carried it shows the move icon, as it did before. -->
            <button
              type="button"
              class="reorder-handle cover-handle"
              tabindex="-1"
              :aria-label="handleLabel"
              @click.stop="emit('grab')"
            >
              <FontAwesomeIcon v-if="!isGrabbed" icon="fas fa-grip-dots-vertical" size="sm" class="grip" />
              <FontAwesomeIcon v-else icon="arrow-down-arrow-up" size="sm" class="arrows" />
            </button>
          </template>
        </v-img>
      </div>
      <div class="info d-flex flex-column justify-space-between my-3 pl-sm-4 pl-2 gc-1">
        <div class="d-flex justify-start gc-1 align-start">
          <v-icon class="my-auto mr-1" :color="contentIconColor" size="16" :icon="contentIcon"></v-icon>
          <span class="title text-truncate"> {{ title }} </span>
        </div>
        <div class="description text-truncate">{{ description }}</div>
        <span v-if="grades || subjectText" class="grades text-truncate">{{ grades }}{{ subjectText }}</span>
        <span v-if="publisher" class="publisher">{{ t(l.library.publisher) }}: {{ publisher }}</span>
      </div>
      <v-spacer />
      <div v-if="isSelecting" class="checkbox-container my-3">
        <v-checkbox-btn
          v-if="isContentInAdded"
          class="my-auto h-100 text-tertiary"
          :model-value="true"
          disabled
          @click.stop
          @keydown.stop
        />
        <v-checkbox-btn
          v-else
          class="my-auto h-100"
          :class="{ 'text-tertiary': isSelected }"
          :model-value="isSelected"
          :aria-label="t('components.select_item', { name: title || '' })"
          @update:model-value="() => emit('toggleSelect')"
          @click.stop
          @keydown.stop
        />
      </div>
      <div v-if="isEditing && isOrdering" class="checkbox-container my-3">
        <FontAwesomeIcon icon="arrow-down-arrow-up" size="sm" class="arrows my-auto" />
      </div>
      <div v-if="isAdding && !isSelecting" class="checkbox-container my-3" :style="{ cursor: 'default' }">
        <div v-if="isContentInAdded" class="fill-height d-flex align-center">
          <span class="text-on-tertiary-container text-no-wrap font-weight-bold">
            <v-icon>mdi-checkbox-marked-circle-outline</v-icon>
            <span v-if="!xs">{{ t(l.components.added) }}</span>
          </span>
        </div>
        <AddCollectionButton
          v-else
          class="my-auto"
          :label="t(l.components.add_button_label, { title: title || '' })"
          @click-add="addContent"
          @click.stop
          @keydown.stop
        />
      </div>
    </div>
    <v-divider class="divider" />
  </template>
</template>

<style scoped lang="scss">
@use '@/foundations/theme/settings';
@use 'sass:map';

.thumb-overlay {
  background-color: rgb(var(--v-theme-inverse-surface));
  opacity: 0.8;
}

.cover-handle:active {
  cursor: grabbing;
}

.cover-handle {
  position: absolute;
  inset: 0;
  width: 100%;
  background: none;
  border: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgb(var(--v-theme-inverse-on-surface));
  cursor: grab;
}

.thumb .grip,
.thumb .arrows {
  width: 16px;
  height: 16px;
  color: rgb(var(--v-theme-inverse-on-surface));
}

.divider {
  color: rgba(var(--v-theme-outline-variant));
}
.item-container {
  height: 100px;
}
.info {
  height: 74px;
  min-width: 0;
  font-size: 0.875rem;
  color: rgb(var(--v-theme-on-surface));
}
.checkbox-container {
  height: 74px;
  min-width: 150px;
  display: flex;
  align-items: center;
  flex-direction: column;
  @media #{map.get(settings.$display-breakpoints, 'xs')} {
    min-width: 75px;
  }
}
.thumb {
  background-color: inherit;

  :deep(.v-img) {
    width: 108px;
    height: 74px;
    @media #{map.get(settings.$display-breakpoints, 'xs')} {
      width: 75px;
      height: 74px;
    }
  }
}
.title {
  font-size: 1rem;
}
.description {
  font-size: 0.75rem;
}
.grades,
.publisher {
  font-size: 0.75rem;
  color: rgb(var(--v-theme-on-surface-medium));
}
</style>
